import { can, type Actor, type FlagName, type Permission, type RecordRef } from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { ApiError, describeError } from './api.ts';
import { signOut as authSignOut } from './auth.ts';
import { api, UNAUTHENTICATED_EVENT, WRITE_EVENT, type Me } from './client.ts';

/**
 * The signed-in session (RELAY-937): who the user is, which organisation they act in, what
 * they may do there (`can`, fed by the API's effective permission set) and which flags are
 * on for them. Both come from the API — the web app never evaluates a flag itself.
 */
export interface Session {
  me: Me;
  actor: Actor;
  can: (permission: Permission, record?: RecordRef) => boolean;
  flags: Partial<Record<FlagName, boolean>>;
  isEnabled: (flag: FlagName) => boolean;
  switchOrganisation: (organisationId: string) => Promise<void>;
  switching: boolean;
  signOut: () => void;
  refresh: () => Promise<unknown>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession() outside <SessionProvider>');
  return s;
}

export function actorOf(me: Me): Actor {
  return {
    userId: me.user.id,
    organisationId: me.organisation.id,
    teamId: me.teamId,
    permissions: new Map(me.permissions.map((g) => [g.permission, g.scope])),
  };
}

export function SessionProvider({
  children,
  onUnauthenticated,
  fallback,
}: {
  children: ReactNode;
  onUnauthenticated: () => void;
  /** What to show while /me loads. */
  fallback?: ReactNode;
}) {
  const qc = useQueryClient();
  // Any successful write refreshes the record timelines on screen (RELAY-946: immediately).
  useEffect(() => {
    const refresh = () => {
      void qc.invalidateQueries({ queryKey: ['activities'] });
      void qc.invalidateQueries({ queryKey: ['candidate-history'] });
    };
    window.addEventListener(WRITE_EVENT, refresh);
    return () => window.removeEventListener(WRITE_EVENT, refresh);
  }, [qc]);
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false, staleTime: 60_000 });
  const flags = useQuery({
    queryKey: ['flags'],
    queryFn: api.flags,
    enabled: me.isSuccess,
    staleTime: 60_000,
  });

  useEffect(() => {
    window.addEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
  }, [onUnauthenticated]);

  const switchMutation = useMutation({
    mutationFn: api.switchOrganisation,
    onSuccess: async (next) => {
      qc.setQueryData(['me'], next);
      // Everything else was fetched in the previous organisation's scope.
      await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    },
  });
  const switchOrganisation = useCallback(
    async (organisationId: string) => {
      await switchMutation.mutateAsync(organisationId);
    },
    [switchMutation],
  );

  const value = useMemo<Session | null>(() => {
    if (!me.data) return null;
    const actor = actorOf(me.data);
    const flagMap: Partial<Record<FlagName, boolean>> = {};
    for (const f of flags.data ?? []) flagMap[f.name] = f.enabled;
    return {
      me: me.data,
      actor,
      can: (permission, record) => can(actor, permission, record),
      flags: flagMap,
      isEnabled: (flag) => flagMap[flag] ?? false,
      switchOrganisation,
      switching: switchMutation.isPending,
      signOut: () => authSignOut(),
      refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
    };
  }, [me.data, flags.data, switchOrganisation, switchMutation.isPending, qc]);

  if (me.isError) {
    const err = me.error;
    // A 401 already dispatched the unauthenticated event; anything else is shown with a retry.
    if (err instanceof ApiError && err.status === 401) return null;
    return (
      <div className="mx-auto mt-24 max-w-md p-6 text-center" role="alert">
        <p className="font-medium">Could not load your account</p>
        <p className="text-muted-foreground mt-1 text-sm">{describeError(err)}</p>
        <button
          type="button"
          className="bg-brand text-brand-foreground mt-4 rounded-md px-4 py-2 text-sm"
          onClick={() => void me.refetch()}
        >
          Try again
        </button>
      </div>
    );
  }
  if (!value || (flags.isPending && !flags.isError)) return <>{fallback ?? null}</>;
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
